import {
  type ContractorResponseType,
  ContractorRoutesRepository,
  type ProgramResponseType,
  ProgramRoutesRepository
} from '@shared/generated';
import { NotificationType } from '@shared/types/Notification';
import pushNotification from '@shared/utils/notification';
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

export const useContractorStore = defineStore('contractor', () => {
  const route = useRoute();
  const { t } = useI18n();
  const loading = ref<boolean>(true);
  const contractor = ref<ContractorResponseType | null>(null);
  const supportedPrograms = ref<ProgramResponseType[]>([]);

  //compute route params and metadata to to avoid stale values
  const isInternalRoute = computed(() => {
    return route.meta.isInternal as string | undefined;
  });
  const currentContractorIdFromInternalRoute = computed(() => {
    return route.params.contractorId as string | undefined;
  });

  const contractorProgramKeys = computed(() => {
    if (contractor.value?.programs) {
      return contractor.value.programs.map((program) => {
        return program.key;
      });
    }
    return [];
  });

  const fetchCurrentContractor = async () => {
    try {
      loading.value = true;
      //If we are on an internal route with contractorId param, use that to fetch contractor
      if (isInternalRoute.value) {
        if (currentContractorIdFromInternalRoute.value) {
          contractor.value =
            await ContractorRoutesRepository.contractorRoutesGet({
              id: currentContractorIdFromInternalRoute.value
            });
          //Get supported programs
          supportedPrograms.value =
            await ProgramRoutesRepository.programRoutesGetAllPrograms({});
        } else {
          pushNotification({
            type: NotificationType.DANGER,
            message: t('common_messages.error.route_missing_contractor_id')
          });
        }
      } else {
        contractor.value =
          await ContractorRoutesRepository.contractorRoutesGetCurrentContractor(
            {}
          );
        //For external routes, supported programs are those linked to the contractor
        supportedPrograms.value = contractor.value.programs;
      }
    } finally {
      loading.value = false;
    }
  };

  const resetContractorStore = () => {
    loading.value = true;
    contractor.value = null;
    supportedPrograms.value = [];
  };

  return {
    loading,
    fetchCurrentContractor,
    contractor,
    resetContractorStore,
    supportedPrograms,
    contractorProgramKeys
  };
});
